import React, { useState, useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import SolicitudLabel from "./SolicitudLabel";
import {
  Card,
  CardBody,
  Col,
  Row,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Input,
  Label,
} from "reactstrap";
import { getFarmaciaPorCodigo, updateSolicitudStatus, aceptarSolicitud } from "../../../helpers/backend_helper";
import logo_sm from "../../../assets/images/logo-sm.png";
import {
  editSolicitud,
  createMessage,
  addComentario,
  resetSiscotelFlag
} from "../../../slices/thunks";
import { limpiarDatosSolicitud } from "../../../utils/solicitudDataCleaner";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/**
 * Section es un componente que se encarga de mostrar los detalles de una
 * solicitud de medicamentos. Recibe como parámetro un objeto con la
 * información de la solicitud y devuelve un JSX con los detalles de la
 * solicitud.
 *
 * @param {object} props - Objeto con la información de la solicitud.
 *
 * @returns {JSX} Un JSX con los detalles de la solicitud.
 */
const Section = ({ solicitud }) => {
  const labelRef = useRef();
  const dispatch = useDispatch();
  const [motivo, setMotivo] = useState("");
  const [motivoDetalle, setMotivoDetalle] = useState("");
  const [errorCancelacion, setErrorCancelacion] = useState("");
  const [procesandoCancelacion, setProcesandoCancelacion] = useState(false);
  const [logoFarmacia, setLogoFarmacia] = useState(null);
  
  // Estados para editar estatus
  const [modalEditarEstatus, setModalEditarEstatus] = useState(false);
  const [nuevoEstatus, setNuevoEstatus] = useState("");
  const [comentarioEstatus, setComentarioEstatus] = useState("");
  const [procesandoEstatus, setProcesandoEstatus] = useState(false);
  const [esAdmin, setEsAdmin] = useState(false);
  
  // Estados para aceptar solicitud
  const [modalAceptarSolicitud, setModalAceptarSolicitud] = useState(false);
  const [procesandoAceptacion, setProcesandoAceptacion] = useState(false);

  // Verificar si el usuario es administrador
  useEffect(() => {
    const authUserStr = localStorage.getItem("authUser");
    if (authUserStr) {
      try {
        const authUser = JSON.parse(authUserStr);
        setEsAdmin(authUser.perfil === "Administrador");
      } catch (error) {
        //console.log("Error al parsear authUser");
      }
    }
  }, []);

  // Cargar logo de la farmacia basado en codigoProveedor
  useEffect(() => {
    const cargarLogoFarmacia = async () => {
      if (solicitud?.codigoProveedor) {
        try {
          const response = await getFarmaciaPorCodigo(solicitud.codigoProveedor);
          if (response?.data?.data?.logo) {
            setLogoFarmacia(response.data.data.logo);
          }
        } catch (error) {
          //console.log("No se pudo cargar el logo de la farmacia");
        }
      }
    };
    cargarLogoFarmacia();
  }, [solicitud?.codigoProveedor]);

  // Lista de motivos predefinidos
  const motivosList = [
    "Paciente no requiere el medicamento",
    "Medicamento no disponible",
    "Cobertura insuficiente para la patología",
    "Error en la solicitud",
    "Solicitud duplicada",
    "Cambio de tratamiento médico",
    "Otro"
  ];

  const selectLayoutState = (state) => state;
  const pageData = createSelector(selectLayoutState, (state) => ({
    siscotel: state.Siscotel.siscotel,
    error: state.Siscotel.error,
    loading: state.Siscotel.loading,
    errorMsg: state.Siscotel.errorMsg,
    colorMsg: state.Siscotel.colorMsg,
    exito: state.Siscotel.exito,
  }));

  const { siscotel, error, loading, errorMsg, colorMsg, exito } =
    useSelector(pageData);

  useEffect(() => {
    if (siscotel && siscotel.status == 200 && motivo && procesandoCancelacion) {
      const now = new Date();
      const creado = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000
      ).toISOString();

      // Construir mensaje completo
      const mensajeCompleto = motivo === "Otro" 
        ? `${motivo}: ${motivoDetalle}`
        : motivo;

      const comentario = {
        tipo: "Enviado",
        mensaje: "Solicitud cancelada por: " + mensajeCompleto,
        estatus: true,
        adjunto: null,
        creado: creado,
      };

      const nuevaSolicitud = {
        ...solicitud,
        estatus: "Cancelado",
        porcentaje_avance: 0, // Resetear porcentaje al cancelar
        comentarios: solicitud.comentarios
          ? [
              ...(Array.isArray(solicitud.comentarios)
                ? solicitud.comentarios
                : []),
              comentario,
            ]
          : [comentario],
      };

      dispatch(editSolicitud(limpiarDatosSolicitud(nuevaSolicitud))).then(() => {
        toast.success("Solicitud cancelada exitosamente");
        setMotivo("");
        setMotivoDetalle("");
        setProcesandoCancelacion(false);
        tog_standard();
      }).catch((error) => {
        console.error("Error al cancelar solicitud:", error);
        toast.error("Error al cancelar la solicitud");
        setProcesandoCancelacion(false);
      });
    }
  }, [dispatch, siscotel, motivo, motivoDetalle, procesandoCancelacion]);

  useEffect(() => {
    if (siscotel.status == "400" || (errorMsg && colorMsg == "danger")) {
      // Muestra el toast con el mensaje de error solo para MyH+
      if (solicitud.aseguradora === "MyH+") {
        toast.error(`API Myh+: ${error}`, {
          autoClose: 8000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          className: "bg-danger text-white",
          icon: ({ theme, type }) => (
            <svg
              viewBox="0 0 24 24"
              width="100%"
              height="100%"
              fill="white"
              style={{ color: "white" }}
            >
              <path d="M11.983 0a12.206 12.206 0 00-8.51 3.653A11.8 11.8 0 000 12.207 11.779 11.779 0 0011.8 24h.214A12.111 12.111 0 0024 11.791 11.766 11.766 0 0011.983 0zM10.5 16.542a1.476 1.476 0 011.449-1.53h.027a1.527 1.527 0 011.523 1.47 1.475 1.475 0 01-1.449 1.53h-.027a1.529 1.529 0 01-1.523-1.47zM11 12.5v-6a1 1 0 012 0v6a1 1 0 11-2 0z" />
            </svg>
          ),
        });

        setTimeout(() => {
          dispatch(resetSiscotelFlag());
        }, 10000);
      }
    }
  }, [dispatch, errorMsg]);

  const getBadgeColor = (priority) => {
    if (!priority) return "info";

    switch (priority.toLowerCase()) {
      case "alta":
        return "danger";

      case "media":
        return "warning";

      case "baja":
        return "info";

      case "nueva solicitud":
        return "primary";

      case "por entregar":
        return "secondary";

      case "en transito":
        return "info";

      case "entregado":
        return "success";

      case "cancelado":
        return "danger";

      default:
        return "info";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const [modal_standard, setmodal_standard] = useState(false);

  function tog_standard() {
    // Limpiar estados al abrir/cerrar modal
    if (!modal_standard) {
      // Al abrir
      setMotivo("");
      setMotivoDetalle("");
      setErrorCancelacion("");
      setProcesandoCancelacion(false);
    }
    setmodal_standard(!modal_standard);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorCancelacion("");

    // Validar que se haya seleccionado un motivo
    if (!motivo) {
      setErrorCancelacion("Por favor, seleccione un motivo de cancelación");
      return;
    }

    // Validar que si es "Otro", se haya ingresado el detalle
    if (motivo === "Otro" && !motivoDetalle.trim()) {
      setErrorCancelacion("Por favor, especifique el motivo de cancelación");
      return;
    }

    // Validar si hay delivery activo
    const deliveryActivo = solicitud.delivery?.some(d => 
      d.estatus === "En Transito" || d.estatus === "Asignado"
    );

    if (deliveryActivo) {
      toast.warning(
        "⚠️ Esta solicitud tiene un delivery activo. " +
        "Por favor, cancele el delivery primero desde la vista del motorizado.",
        {
          position: "top-right",
          autoClose: 6000,
        }
      );
      return;
    }

    // Construir mensaje para SISCOTEL
    const mensajeCompleto = motivo === "Otro" 
      ? `${motivo}: ${motivoDetalle}`
      : motivo;

    if (solicitud.aseguradora === "Seguros Mercantil" || solicitud?.aseguradora?.toLowerCase() === "hispana de seguros" || solicitud?.aseguradora?.toLowerCase() === "real seguros") {
      setProcesandoCancelacion(true);
      try {
        // Primero actualizar el estatus a Cancelado
        const now = new Date();
        const creado = new Date(
          now.getTime() - now.getTimezoneOffset() * 60000
        ).toISOString();

        const comentarioCancelacion = {
          tipo: "Enviado",
          mensaje: `Solicitud cancelada: ${mensajeCompleto}`,
          estatus: true,
          adjunto: null,
          creado: creado,
        };

        const statusUpdate = {
          identificador: solicitud.identificador,
          estatus: "Cancelado",
          comentarios: [comentarioCancelacion]
        };

        await updateSolicitudStatus(statusUpdate);
        
        toast.success("Solicitud cancelada exitosamente");
        
        // Recargar la página para reflejar los cambios
        setTimeout(() => {
          window.location.reload();
        }, 1500);
        
      } catch (error) {
        console.error("Error al cancelar solicitud:", error);
        toast.error(`Error al cancelar la solicitud: ${error.message || "Error desconocido"}`);
        setProcesandoCancelacion(false);
      }
    } else {
      const message = {
        IdentificadorEvento: solicitud.identificador,
        Tipo: "Cancelada",
        Mensaje: mensajeCompleto,
        Adjunto: "",
        CodigoProveedor: solicitud.codigoProveedor,
      };
      setProcesandoCancelacion(true);
      try {
        await dispatch(createMessage(message));
      } catch (error) {
        if (solicitud.aseguradora === "MyH+") {
          toast.error(`Error en MyH+: ${error.message || "Error al procesar cancelación"}`);
        }
        setProcesandoCancelacion(false);
      }
    }
  };

  const handleSendMessage = async () => {
    if (solicitud.aseguradora === "Seguros Mercantil" || solicitud?.aseguradora?.toLowerCase() === "hispana de seguros" || solicitud?.aseguradora?.toLowerCase() === "real seguros") {
      const comentario = {
        tipo: "Enviado",
        mensaje: "Solicitud Aceptada",
        estatus: true,
        adjunto: null,
        creado: null,
      };
      dispatch(addComentario({ identificador: String(solicitud.identificador), comentario }));
      return;
    }


    const message = {
      IdentificadorEvento: solicitud.identificador,
      Tipo: "Mensaje",
      Mensaje: "Solicitud Aceptada",
      Adjunto: "",
      CodigoProveedor: solicitud.codigoProveedor,
    };

    try {
      await dispatch(createMessage(message));
    } catch (error) {
      if (solicitud.aseguradora === "MyH+") {
        toast.error(`Error en MyH+: ${error.message || "Error al enviar el mensaje"}`);
      }
    }
  };

  // Lista de estatus disponibles
  const estatusList = [
    "Nueva Solicitud",
    "En Transito",
    "Entregado",
    "Cancelado",
    "Por Renovar",
    "Por Reprogramar"
  ];

  // Función para abrir/cerrar modal de editar estatus
  const toggleModalEditarEstatus = () => {
    if (!modalEditarEstatus) {
      setNuevoEstatus(solicitud.estatus || "");
      setComentarioEstatus("");
    }
    setModalEditarEstatus(!modalEditarEstatus);
  };

  // Función para abrir/cerrar modal de aceptar solicitud
  const toggleModalAceptarSolicitud = () => {
    setModalAceptarSolicitud(!modalAceptarSolicitud);
  };

  // Función para aceptar la solicitud
  const handleAceptarSolicitud = async (e) => {
    e.preventDefault();
    setProcesandoAceptacion(true);

    try {
      await aceptarSolicitud(solicitud.identificador);
      
      toast.success("Solicitud aceptada exitosamente. Se ha notificado al equipo vía Telegram.");
      
      // Recargar la página para reflejar los cambios
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error("Error al aceptar solicitud:", error);
      toast.error(`Error al aceptar la solicitud: ${error.response?.data?.detail || error.message || "Error desconocido"}`);
      setProcesandoAceptacion(false);
    }
  };

  // Función para actualizar el estatus
  const handleActualizarEstatus = async (e) => {
    e.preventDefault();
    
    if (!nuevoEstatus) {
      toast.error("Por favor, seleccione un estatus");
      return;
    }

    if (nuevoEstatus === solicitud.estatus) {
      toast.warning("El estatus seleccionado es el mismo que el actual");
      return;
    }

    setProcesandoEstatus(true);

    try {
      const now = new Date();
      const creado = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000
      ).toISOString();

      const comentarios = comentarioEstatus.trim() ? [{
        tipo: "Enviado",
        mensaje: `Estatus cambiado de "${solicitud.estatus}" a "${nuevoEstatus}". ${comentarioEstatus}`,
        estatus: true,
        adjunto: null,
        creado: creado,
      }] : [{
        tipo: "Enviado",
        mensaje: `Estatus cambiado de "${solicitud.estatus}" a "${nuevoEstatus}"`,
        estatus: true,
        adjunto: null,
        creado: creado,
      }];

      const statusUpdate = {
        identificador: solicitud.identificador,
        estatus: nuevoEstatus,
        comentarios: comentarios
      };

      await updateSolicitudStatus(statusUpdate);
      
      toast.success(`Estatus actualizado a "${nuevoEstatus}" exitosamente`);
      
      // Recargar la página para reflejar los cambios
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error("Error al actualizar estatus:", error);
      toast.error(`Error al actualizar el estatus: ${error.message || "Error desconocido"}`);
    } finally {
      setProcesandoEstatus(false);
    }
  };

  const [showLabel, setShowLabel] = useState(false);

  const handlePrint = () => {
    const printContent = labelRef.current;

    if (printContent) {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>Etiqueta ${solicitud.identificador}</title>
            
          </head>
          <body>
            <div class="print-container">
              ${printContent.outerHTML}
            </div>
            <script>
              window.onload = function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                }
              }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    } else {
      console.error("No se encontró el contenido para imprimir");
    }
  };

  return (
    <React.Fragment>
      <Col lg={12}>
        {" "}
        <Card className="mt-n4 mx-n4 mb-n5">
          <div className="bg-success-subtle">
            <CardBody className="pb-4 mb-5">
              <Row>
                <div className="col-md">
                  <Row className="align-items-center">
                    <div className="col-md-auto">
                      <div className="avatar-md mb-md-0 mb-4">
                        <div className="avatar-title bg-white rounded" style={{ padding: '8px' }}>
                          <img
                            src={logoFarmacia || logo_sm}
                            alt=""
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            onError={(e) => { e.target.src = logo_sm; }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md">
                      <h4 className="fw-semibold" id="ticket-title">
                        #{solicitud.identificador} - Solicitud:{" "}
                        {solicitud.solicitante} - C.I:{" "}
                        {solicitud.solicitanteCedula}
                      </h4>
                      <div className="hstack gap-3 flex-wrap">
                        <div className="text-muted">
                          {" "}
                          Titular:
                          <span className="fw-medium" id="titular">
                            {" "}
                            {solicitud.titular}
                          </span>{" "}
                          C.I:
                          <span className="fw-medium" id="cedula">
                            {" "}
                            {solicitud.titularCedula}
                          </span>{" "}
                        </div>
                        <div className="text-muted">
                          {" "}
                          {(() => {
                            const tel = solicitud?.telefono || '';
                            const cleanTel = tel.replace(/[\s\-\(\)]/g, '');
                            const isVzlaCelular = /^0(412|414|416|422|424|426)\d{7}$/.test(cleanTel);
                            if (isVzlaCelular) {
                              const waNumber = '58' + cleanTel.substring(1);
                              return (
                                <a
                                  href={`https://wa.me/${waNumber}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-soft-success btn-sm rounded-pill"
                                >
                                  <i className="ri-whatsapp-line me-1"></i>
                                  {tel}
                                </a>
                              );
                            }
                            return (
                              <a
                                href={`tel:${tel}`}
                                className="btn btn-soft-primary btn-sm rounded-pill"
                              >
                                <i className="ri-phone-line me-1"></i>
                                {tel}
                              </a>
                            );
                          })()}

                        </div>

                        {solicitud?.correoElectronico && (
                          <div className="text-muted">
                            <a
                              href={`mailto:${solicitud.correoElectronico}`}
                              className="btn btn-soft-info btn-sm rounded-pill"
                            >
                              <i className="ri-mail-line me-1"></i>
                              {solicitud.correoElectronico}
                            </a>
                          </div>
                        )}

                        <div className="text-muted d-flex align-items-center gap-2">
                          {" "}
                          <Badge
                            pill
                            className="fs-12"
                            color={getBadgeColor(solicitud.estatus)}
                          >
                            {solicitud.estatus}
                          </Badge>
                          {esAdmin && (
                            <Button
                              size="sm"
                              color="primary"
                              outline
                              className="btn-icon"
                              onClick={toggleModalEditarEstatus}
                              title="Editar estatus"
                            >
                              <i className="ri-edit-line"></i>
                            </Button>
                          )}
                        </div>

                        <div className="text-muted">

                          <Badge
                            pill
                            className="fs-12"
                            color={getBadgeColor(solicitud.prioridad)}
                          >
                            {solicitud.prioridad}
                          </Badge>
                        </div>
                        <div className="vr"></div>

                        {/* Indicador de permiso excedente pendiente */}
                        {solicitud.facturacion?.some(f => f.permisoExcedente?.pendiente === true) && (
                          <span
                            className="btn btn-soft-warning btn-sm"
                            title="Permiso de Seguros Mercantil pendiente por monto excedente"
                          >
                            <i className="ri-mail-send-line me-1 fs-16"></i>
                            Permiso pendiente
                          </span>
                        )}

                        {/* Indicador de comentarios nuevos */}
                        {solicitud.comentarios?.filter(c => c.estatus === true && c.tipo !== "Enviado").length > 0 && (
                          <a
                            href="#comentarios-section"
                            className="btn btn-soft-danger btn-sm position-relative"
                            title="Ver comentarios nuevos"
                            onClick={(e) => {
                              e.preventDefault();
                              document.querySelector('.comentarios-container')?.scrollIntoView({ 
                                behavior: 'smooth', 
                                block: 'start' 
                              });
                            }}
                          >
                            <i className="ri-message-3-line fs-16"></i>
                            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                              {solicitud.comentarios.filter(c => c.estatus === true && c.tipo !== "Enviado").length}
                              <span className="visually-hidden">comentarios nuevos</span>
                            </span>
                          </a>
                        )}

                        {solicitud.aseguradora === "Seguros Mercantil" && 
                        solicitud.estatus === "Nueva Solicitud" && (
                          <Button
                            size="sm"
                            color="success"
                            onClick={toggleModalAceptarSolicitud}
                          >
                            <i className="ri-check-line label-icon align-middle fs-16 me-2"></i>
                            Aceptar Solicitud
                          </Button>
                        )}

                        {solicitud.estatus === "Cancelado" ||
                        solicitud.estatus === "Entregado" ||
                        (solicitud.aseguradora === "Seguros Mercantil" && solicitud.estatus === "En Transito") ? (
                          ""
                        ) : (
                          <Button
                            size="sm"
                            color="danger"
                            onClick={() => tog_standard()}
                          >
                            <i className="ri-error-warning-line label-icon align-middle fs-16 me-2"></i>
                            Cancelar la Solicitud
                          </Button>
                        )}

                        {solicitud.facturacion &&
                        solicitud.facturacion.length > 0 ? (
                          <Button
                            size="sm"
                            color="primary"
                            onClick={handlePrint}
                          >
                            <i className="ri-printer-line align-middle fs-16 me-2"></i>
                            Imprimir Etiqueta
                          </Button>
                        ) : (
                          ""
                        )}

                        {/* Componente de la etiqueta */}
                        <div style={{ display: "none" }}>
                          <SolicitudLabel
                            ref={labelRef}
                            solicitud={solicitud}
                            logoFarmacia={logoFarmacia}
                          />
                        </div>
                      </div>
                    </div>
                  </Row>
                </div>
              </Row>
            </CardBody>
          </div>
        </Card>
      </Col>

      <Modal
        id="myModal"
        isOpen={modal_standard}
        toggle={() => {
          tog_standard();
        }}
      >
        <Form onSubmit={handleSubmit}>
          <ModalHeader>
            <h5 className="modal-title" id="myModalLabel">
              Cancelar una Solicitud
            </h5>
          </ModalHeader>
          <ModalBody>
            {errorCancelacion && (
              <div className="alert alert-danger mb-3">
                <i className="ri-error-warning-line me-2"></i>
                {errorCancelacion}
              </div>
            )}

            <div className="mb-3">
              <Label className="form-label fw-semibold">
                <i className="ri-message-2-line me-1"></i>
                Motivo de cancelación de la solicitud #{solicitud.identificador}
              </Label>
              <Input
                type="select"
                value={motivo}
                onChange={(e) => {
                  setMotivo(e.target.value);
                  setErrorCancelacion("");
                }}
                className="form-select"
                disabled={procesandoCancelacion}
              >
                <option value="">Seleccione un motivo</option>
                {motivosList.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Input>
            </div>

            {motivo === "Otro" && (
              <div className="mb-3">
                <Label className="form-label fw-semibold">
                  Especifique el motivo
                </Label>
                <Input
                  type="textarea"
                  rows="3"
                  value={motivoDetalle}
                  onChange={(e) => {
                    setMotivoDetalle(e.target.value);
                    setErrorCancelacion("");
                  }}
                  placeholder="Ingrese el motivo específico de cancelación..."
                  className="form-control"
                  disabled={procesandoCancelacion}
                />
              </div>
            )}

            <div className="alert alert-warning mb-0">
              <i className="ri-alert-line me-2"></i>
              <strong>Advertencia:</strong> Esta acción no se puede deshacer. La solicitud será marcada como cancelada y se notificará al seguro
            </div>
          </ModalBody>
          <ModalFooter className="border-top bg-light p-3">
            <Button
              color="light"
              type="button"
              onClick={tog_standard}
              disabled={procesandoCancelacion}
            >
              <i className="ri-close-line align-bottom me-1"></i>
              Volver
            </Button>
            <Button 
              color="danger" 
              type="submit"
              disabled={!motivo || (motivo === "Otro" && !motivoDetalle.trim()) || procesandoCancelacion}
            >
              {procesandoCancelacion ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Procesando...
                </>
              ) : (
                <>
                  <i className="ri-error-warning-line align-bottom me-1"></i>
                  Sí, Cancelar Solicitud
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Modal para editar estatus */}
      <Modal
        isOpen={modalEditarEstatus}
        toggle={toggleModalEditarEstatus}
      >
        <Form onSubmit={handleActualizarEstatus}>
          <ModalHeader toggle={toggleModalEditarEstatus}>
            <h5 className="modal-title">
              <i className="ri-edit-line me-2"></i>
              Editar Estatus de Solicitud
            </h5>
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label className="form-label fw-semibold">
                Solicitud #{solicitud.identificador}
              </Label>
              <div className="alert alert-info mb-3">
                <i className="ri-information-line me-2"></i>
                Estatus actual: <strong>{solicitud.estatus}</strong>
              </div>
            </div>

            <div className="mb-3">
              <Label className="form-label fw-semibold">
                <i className="ri-list-check me-1"></i>
                Nuevo Estatus <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                value={nuevoEstatus}
                onChange={(e) => setNuevoEstatus(e.target.value)}
                className="form-select"
                disabled={procesandoEstatus}
                required
              >
                <option value="">Seleccione un estatus</option>
                {estatusList.map((estatus) => (
                  <option key={estatus} value={estatus}>
                    {estatus}
                  </option>
                ))}
              </Input>
            </div>

            <div className="mb-3">
              <Label className="form-label fw-semibold">
                <i className="ri-message-2-line me-1"></i>
                Comentario (opcional)
              </Label>
              <Input
                type="textarea"
                rows="3"
                value={comentarioEstatus}
                onChange={(e) => setComentarioEstatus(e.target.value)}
                placeholder="Agregue un comentario sobre el cambio de estatus..."
                disabled={procesandoEstatus}
              />
            </div>

            <div className="alert alert-warning mb-0">
              <i className="ri-alert-line me-2"></i>
              <strong>Nota:</strong> El cambio de estatus se registrará en el historial de comentarios de la solicitud.
            </div>
          </ModalBody>
          <ModalFooter className="border-top bg-light p-3">
            <Button
              color="light"
              type="button"
              onClick={toggleModalEditarEstatus}
              disabled={procesandoEstatus}
            >
              <i className="ri-close-line align-bottom me-1"></i>
              Cancelar
            </Button>
            <Button 
              color="primary" 
              type="submit"
              disabled={!nuevoEstatus || nuevoEstatus === solicitud.estatus || procesandoEstatus}
            >
              {procesandoEstatus ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Actualizando...
                </>
              ) : (
                <>
                  <i className="ri-check-line align-bottom me-1"></i>
                  Actualizar Estatus
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Modal para aceptar solicitud */}
      <Modal
        isOpen={modalAceptarSolicitud}
        toggle={toggleModalAceptarSolicitud}
      >
        <Form onSubmit={handleAceptarSolicitud}>
          <ModalHeader toggle={toggleModalAceptarSolicitud}>
            <h5 className="modal-title">
              <i className="ri-check-line me-2"></i>
              Aceptar Solicitud
            </h5>
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label className="form-label fw-semibold">
                Solicitud #{solicitud.identificador}
              </Label>
              <div className="alert alert-info mb-3">
                <i className="ri-information-line me-2"></i>
                Solicitante: <strong>{solicitud.solicitante}</strong>
              </div>
            </div>

            <div className="alert alert-success mb-3">
              <i className="ri-check-line me-2"></i>
              <strong>Acciones que se ejecutarán:</strong>
              <ul className="mb-0 mt-2">
                <li>El estatus cambiará a <strong>"En Transito"</strong></li>
                <li>Se agregará un comentario de aceptación</li>
                <li>Se enviará notificación al equipo vía <strong>Telegram</strong></li>
              </ul>
            </div>

            <div className="alert alert-warning mb-0">
              <i className="ri-alert-line me-2"></i>
              <strong>Importante:</strong> Esta acción indica que acepta gestionar esta solicitud. 
              Recuerde gestionar el caso en la página de Mercantil.
            </div>
          </ModalBody>
          <ModalFooter className="border-top bg-light p-3">
            <Button
              color="light"
              type="button"
              onClick={toggleModalAceptarSolicitud}
              disabled={procesandoAceptacion}
            >
              <i className="ri-close-line align-bottom me-1"></i>
              Cancelar
            </Button>
            <Button 
              color="success" 
              type="submit"
              disabled={procesandoAceptacion}
            >
              {procesandoAceptacion ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Procesando...
                </>
              ) : (
                <>
                  <i className="ri-check-line align-bottom me-1"></i>
                  Sí, Aceptar Solicitud
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default Section;
